/**
 * THE CHECKOUT ATTEMPT ID — one idempotency key per attempt, not per cart.
 *
 * WHY NOT THE CART'S CONTENTS. The first cut derived the key from the lines, which is safe against a
 * double-click and WRONG against a customer: the same person legitimately buying the same journey a
 * second time would hash to the same key and be handed back their OLD order, permanently. A cart
 * fingerprint is an identity for the goods, not for the purchase.
 *
 * WHAT AN ATTEMPT IS. It begins when checkout begins and survives exactly the things that are the
 * same attempt — a double-click, a refresh, a retry after a refusal. It ends on success, and it is
 * rotated when the cart is materially restarted or when it goes stale, so a later purchase is a new
 * attempt rather than a replay of an old one.
 *
 * The server namespaces this by customer, so two people can never collide on one id even if a client
 * repeated it.
 */
const KEY = "sg.checkout.attempt";
/** Long enough to survive a slow payment page and a retry; far too short to span a later purchase. */
const TTL_MS = 2 * 60 * 60 * 1000;

type Attempt = { id: string; fingerprint: string; at: number };

const read = (): Attempt | null => {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return null;
    const a = JSON.parse(raw) as Attempt;
    return a && typeof a.id === "string" ? a : null;
  } catch {
    return null;   // private mode, cleared storage, corrupt value — start a fresh attempt
  }
};

const write = (a: Attempt) => {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(a));
  } catch {
    /* storage unavailable: the id still works for THIS page's double-clicks, which is the common case */
  }
};

const uuid = (): string => {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  } catch { /* fall through */ }
  return `att-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
};

/**
 * The id for the attempt in progress, creating one when there is none.
 *
 * `fingerprint` is what "materially restarted cart" means in practice: change what you are buying and
 * you are on a new attempt. It is NOT the idempotency key — it only decides when to rotate.
 */
export function currentCheckoutAttempt(fingerprint: string): string {
  const existing = read();
  const stale = !existing || Date.now() - existing.at > TTL_MS;
  const restarted = !!existing && existing.fingerprint !== fingerprint;
  if (existing && !stale && !restarted) return existing.id;
  const next: Attempt = { id: uuid(), fingerprint, at: Date.now() };
  write(next);
  return next.id;
}

/**
 * The attempt is over — success, cancellation, or an expiry the caller observed. The NEXT checkout
 * starts a new attempt, which is what stops a legitimate repeat purchase replaying this one's order.
 */
export function endCheckoutAttempt(): void {
  try {
    window.localStorage.removeItem(KEY);
  } catch {
    /* nothing to clear */
  }
}

/** W17 — the id of the attempt in progress, or null. Never creates or rotates one. */
export function peekCheckoutAttempt(): string | null {
  return read()?.id ?? null;
}

/**
 * W17 — ends the attempt only while it is still `id` (the attempt that created the order being settled). Settling an
 * older order — a redirect return, a status check — never ends a newer checkout's attempt; an unknown owner ends nothing.
 */
export function endCheckoutAttemptIf(id: string | null): void {
  if (id && read()?.id === id) endCheckoutAttempt();
}
